/**
 * istylux — Animation & Interaction Layer
 * Handles scroll reveals, header state, and shared motion utilities.
 */
(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* -----------------------------------------------------------------------
     Scroll reveal via IntersectionObserver
     ----------------------------------------------------------------------- */
  function initRevealAnimations() {
    if (REDUCED_MOTION) {
      document.querySelectorAll('.reveal, .reveal-up, .reveal-fade, .reveal-scale, .is-stagger-children').forEach((el) => {
        el.classList.add('is-visible');
      });
      return;
    }

    const revealSelectors = '.reveal, .reveal-up, .reveal-fade, .reveal-scale, .is-stagger-children';
    const elements = document.querySelectorAll(revealSelectors);

    if (!elements.length || !('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.12,
      }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* -----------------------------------------------------------------------
     Header scroll class toggle
     ----------------------------------------------------------------------- */
  function initHeaderScroll() {
    const headerGroup = document.querySelector('.shopify-section-group-header-group');
    if (!headerGroup) return;

    let ticking = false;
    const threshold = 40;

    const updateHeader = () => {
      if (window.scrollY > threshold) {
        headerGroup.classList.add('is-header-scrolled');
        document.documentElement.classList.add('is-scrolled');
      } else {
        headerGroup.classList.remove('is-header-scrolled');
        document.documentElement.classList.remove('is-scrolled');
      }
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(updateHeader);
          ticking = true;
        }
      },
      { passive: true }
    );

    updateHeader();
  }

  /* -----------------------------------------------------------------------
     Parallax-safe hero subtle shift (very light, mobile-safe)
     ----------------------------------------------------------------------- */
  function initHeroParallax() {
    if (REDUCED_MOTION) return;

    const heroes = document.querySelectorAll('[data-is-hero-parallax]');
    if (!heroes.length || window.innerWidth < 750) return;

    let ticking = false;

    const update = () => {
      heroes.forEach((hero) => {
        const media = hero.querySelector('.is-hero__media img, .is-lifestyle-banner__media img');
        if (!media) return;

        const rect = hero.getBoundingClientRect();
        const progress = Math.min(Math.max(-rect.top / rect.height, 0), 1);
        const shift = progress * 24;
        media.style.transform = `translateY(${shift}px) scale(1.05)`;
      });
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /* -----------------------------------------------------------------------
     Sticky ATC visibility (basic — extended in istylux-product.js)
     ----------------------------------------------------------------------- */
  function initStickyAtcObserver() {
    const stickyBar = document.querySelector('.is-sticky-atc');
    const buyBox = document.querySelector('[data-is-buy-box]');
    if (!stickyBar || !buyBox || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyBar.classList.toggle('is-active', !entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: '0px' }
    );

    observer.observe(buyBox);
  }

  /* -----------------------------------------------------------------------
     Accordion smooth height helper (optional enhancement)
     ----------------------------------------------------------------------- */
  function initAccordionEnhancements() {
    document.querySelectorAll('.is-accordion details').forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (detail.open) {
          const siblings = detail.closest('.is-accordion')?.querySelectorAll('details[open]');
          siblings?.forEach((sibling) => {
            if (sibling !== detail) sibling.open = false;
          });
        }
      });
    });
  }

  /* -----------------------------------------------------------------------
     Init
     ----------------------------------------------------------------------- */
  function init() {
    initRevealAnimations();
    initHeaderScroll();
    initHeroParallax();
    initStickyAtcObserver();
    initAccordionEnhancements();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init reveals when Shopify theme editor reloads a section */
  document.addEventListener('shopify:section:load', () => {
    initRevealAnimations();
    initHeroParallax();
  });
})();
